import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import React, { useState } from 'react';
import { message } from 'antd';
import { usePutAssetMutation, usePutAssetBatchMutation } from '../../features/api/apiSlice';

export default function AssignLayer({
  modalOpen,
  setModalOpen,
  selectedAsset,
  setSelectedAsset,
  assetsSelected = [],
  setAssetsSelected,
  layers,
  refetch,
}) {
  const [updateAsset] = usePutAssetMutation();
  const [updateAssetBatch] = usePutAssetBatchMutation();
  const [selectedLayer, setSelectedLayer] = useState(null);
  const [isAssigning, setIsAssigning] = useState(false);

  const isBulk = !selectedAsset && assetsSelected.length > 0;
  const assets = selectedAsset ? [selectedAsset] : assetsSelected;
  const hasAssets = assets.length > 0;

  const handleLayerSelect = async () => {
    if (!selectedLayer) {
      message.warning('Please select a layer first.');
      return;
    }
    if (!hasAssets) {
      message.warning('Please select at least one asset.');
      return;
    }

    setIsAssigning(true);
    try {
      const projectId = assets[0].project_id;

      if (isBulk) {
        await updateAssetBatch({
          projectId,
          asset_ids: assets.map((a) => a.id),
          drawing_id: selectedLayer.id,
        }).unwrap();
        message.success(`Assigned "${selectedLayer.name}" to ${assets.length} asset(s).`);
        if (typeof setAssetsSelected === 'function') setAssetsSelected([]);
      } else {
        await updateAsset({
          projectId,
          assetId: selectedAsset.id,
          drawing_id: selectedLayer.id,
        }).unwrap();
        message.success(`Assigned "${selectedLayer.name}" to "${selectedAsset.name || 'asset'}".`);
        if (typeof setSelectedAsset === 'function') setSelectedAsset(null);
      }

      if (typeof refetch === 'function') await refetch();
      setSelectedLayer(null);
      setModalOpen(false);
    } catch (error) {
      console.error('Failed to assign layer:', error);
      message.error('Failed to assign layer. Please try again.');
    } finally {
      setIsAssigning(false);
    }
  };

  const selectedLayerTemplate = (option) => {
    if (!option) return <span>Assign a Layer</span>;

    return (
      <div
        style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0, width: '100%' }}
      >
        <span
          style={{
            width: '12px',
            height: '12px',
            borderRadius: '2px',
            backgroundColor: option.color,
            display: 'inline-block',
            flexShrink: 0,
          }}
        />
        <span
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            minWidth: 0,
          }}
          title={option.name}
        >
          {option.name}
        </span>
      </div>
    );
  };

  const layerOptionTemplate = (option) => {
    if (!option) return;

    return (
      <div
        style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0, width: '100%' }}
      >
        <span
          style={{
            width: '12px',
            height: '12px',
            borderRadius: '2px',
            backgroundColor: option.color,
            display: 'inline-block',
            flexShrink: 0,
          }}
        />
        <span
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            minWidth: 0,
          }}
          title={option.name}
        >
          {option.name}
        </span>
      </div>
    );
  };

  if (!modalOpen) return null;

  if (!hasAssets) return null;

  return (
    <div
      role="presentation"
      onMouseDown={(e) => {
        e.stopPropagation();
        if (e.target === e.currentTarget) setModalOpen(false);
      }}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        zIndex: 1001,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div
        style={{
          backgroundColor: '#fff',
          padding: '24px',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          width: 'min(560px, 92vw)',
          maxWidth: '90%',
        }}
      >
        <h3 style={{ marginBottom: '4px', fontSize: '18px', fontWeight: '600' }}>Assign a Layer</h3>
        <p style={{ margin: '0 0 16px', fontSize: '13px', color: '#6b7280' }}>
          {isBulk ? `${assets.length} asset(s) selected` : assets[0]?.name || 'Selected asset'}
        </p>

        <Dropdown
          value={selectedLayer}
          onChange={(e) => setSelectedLayer(e.value)}
          options={layers}
          optionLabel="name"
          placeholder="Select a layer"
          filter
          valueTemplate={selectedLayerTemplate}
          itemTemplate={layerOptionTemplate}
          appendTo="self"
          style={{
            width: '100%',
            borderRadius: '6px',
            padding: '8px',
            border: '1px solid #ccc',
            boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)',
          }}
          panelStyle={{ width: '100%', maxWidth: '100%' }}
          panelClassName="layer-panel"
        />

        <div
          style={{
            marginTop: '24px',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '12px',
          }}
        >
          <Button
            icon="pi pi-times"
            severity="danger"
            onClick={() => setModalOpen(false)}
            label="Cancel"
          />
          <Button
            icon="pi pi-check"
            onClick={handleLayerSelect}
            label="Assign"
            loading={isAssigning}
            disabled={!selectedLayer || isAssigning}
          />
        </div>
      </div>
    </div>
  );
}
